import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, EstadoVacio } from '@/components/Card';
import { Falta } from '@/components/Falta';
import { TextField, NumberField, MoneyField, SelectField } from '@/components/Field';
import { IngredientForm } from './IngredientForm';
import { listarInsumos, registrarSaldoInicial } from '@/db/repos/ingredients';
import { describirRenglon } from '@/db/repos/inventory';
import { unidadesDisponibles, etiquetaUnidad } from '@/domain/units';
import { formatearCentavos, textoACentavos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';
import type { Ingredient } from '@/db/types';

type Vista =
  | { modo: 'lista' }
  | { modo: 'crear' }
  | { modo: 'editar'; insumo: Ingredient }
  | { modo: 'saldo'; insumo: Ingredient };

export function IngredientsPage() {
  const insumos = useLiveQuery(() => listarInsumos(), []);
  const [vista, setVista] = useState<Vista>({ modo: 'lista' });
  const [filtro, setFiltro] = useState('');

  if (vista.modo === 'crear' || vista.modo === 'editar') {
    return (
      <div className="space-y-5">
        <PageHeader
          titulo={vista.modo === 'crear' ? 'Nuevo insumo' : 'Editar insumo'}
          descripcion="Solo hacen falta dos datos"
        />
        <IngredientForm
          {...(vista.modo === 'editar' ? { insumo: vista.insumo } : {})}
          onGuardado={() => setVista({ modo: 'lista' })}
          onCancelar={() => setVista({ modo: 'lista' })}
        />
      </div>
    );
  }

  if (vista.modo === 'saldo') {
    return (
      <SaldoInicialForm
        insumo={vista.insumo}
        onListo={() => setVista({ modo: 'lista' })}
        onCancelar={() => setVista({ modo: 'lista' })}
      />
    );
  }

  const visibles =
    insumos?.filter((i) =>
      filtro.trim() === '' ? true : i.nombre.toLowerCase().includes(filtro.toLowerCase()),
    ) ?? [];

  return (
    <div className="space-y-5">
      <PageHeader titulo="Insumos" descripcion="Lo que compras para cocinar" />

      <Button tamano="grande" onClick={() => setVista({ modo: 'crear' })}>
        + Nuevo insumo
      </Button>

      {insumos !== undefined && insumos.length > 3 && (
        <TextField etiqueta="Buscar" valor={filtro} onCambio={setFiltro} placeholder="pollo…" />
      )}

      {insumos === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

      {insumos !== undefined && insumos.length === 0 && (
        <EstadoVacio
          titulo="Todavía no hay insumos"
          descripcion="Empieza con los que usas más seguido. También puedes crearlos mientras registras una compra."
        />
      )}

      {visibles.length > 0 && (
        <ul className="space-y-2">
          {visibles.map((insumo) => {
            const renglon = describirRenglon(insumo);
            return (
              <li key={insumo.id}>
                <Card>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-texto">{insumo.nombre}</p>
                      <p className="text-sm text-texto-suave">
                        {renglon.existencia}
                        {renglon.costoPorUnidadCompra !== null && (
                          <>
                            {' · '}
                            {formatearCentavos(renglon.costoPorUnidadCompra)} /{' '}
                            {renglon.unidadCosto}
                          </>
                        )}
                      </p>
                      {renglon.sinCosto !== null && (
                        <p className="mt-0.5 text-xs text-aviso">
                          {renglon.sinCosto} sin costo registrado
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <Button
                      variante="secundario"
                      completo={false}
                      onClick={() => setVista({ modo: 'saldo', insumo })}
                    >
                      Saldo inicial
                    </Button>
                    <Button
                      variante="fantasma"
                      completo={false}
                      onClick={() => setVista({ modo: 'editar', insumo })}
                    >
                      Editar
                    </Button>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsSaldo {
  insumo: Ingredient;
  onListo: () => void;
  onCancelar: () => void;
}

/**
 * Saldo inicial: lo que ya se tenia antes de usar la aplicacion (Req. 8).
 *
 * El costo es OPCIONAL. Sin el, la cantidad entra al inventario fisico pero no
 * al acervo valuado, asi que no abarata el costo promedio. La pantalla lo
 * explica porque es una distincion que afecta los numeros del negocio.
 */
function SaldoInicialForm({ insumo, onListo, onCancelar }: PropsSaldo) {
  const [cantidad, setCantidad] = useState('');
  const [unidad, setUnidad] = useState(insumo.unidadCompraPreferida);
  const [conoceCosto, setConoceCosto] = useState(false);
  const [costo, setCosto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const opciones = unidadesDisponibles({
    dimension: insumo.dimension,
    factorEquivalencia: insumo.factorEquivalencia,
  }).map((u) => ({ valor: u, etiqueta: etiquetaUnidad(u, 2) }));

  const cantidadNumero = Number(cantidad);

  const faltan: string[] = [];
  if (!Number.isFinite(cantidadNumero) || cantidadNumero <= 0) faltan.push('la cantidad');
  const valida = faltan.length === 0;

  async function guardar() {
    setGuardando(true);
    setError(null);

    try {
      await registrarSaldoInicial({
        ingredientId: insumo.id,
        cantidadCaptura: cantidadNumero,
        unidadCaptura: unidad,
        costoCentavos: conoceCosto ? textoACentavos(costo) : null,
      });
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        titulo="Saldo inicial"
        descripcion={`Cuánto ${insumo.nombre} ya tienes antes de empezar a registrar compras`}
      />

      <NumberField
        etiqueta="Cantidad que tienes"
        valor={cantidad}
        onCambio={setCantidad}
        placeholder="4"
        autoFocus
      />

      <SelectField etiqueta="Unidad" valor={unidad} opciones={opciones} onCambio={setUnidad} />

      <Card>
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={conoceCosto}
            onChange={(e) => setConoceCosto(e.target.checked)}
            className="mt-0.5 size-5 shrink-0"
          />
          <span>
            <span className="font-medium text-texto">Sé cuánto me costó</span>
            <span className="mt-1 block text-texto-suave">
              Si no lo sabes, déjalo sin marcar. La cantidad va a contar para cocinar, pero no
              va a afectar tus costos promedio con un precio inventado.
            </span>
          </span>
        </label>

        {conoceCosto && (
          <div className="mt-3">
            <MoneyField
              etiqueta="Cuánto costó en total"
              valor={costo}
              onCambio={setCosto}
              ayuda="El precio de toda la cantidad, no por unidad"
            />
          </div>
        )}
      </Card>

      {!conoceCosto && valida && (
        <Banner tono="info" titulo="Se registrará sin costo">
          Esta cantidad va a aparecer en tu inventario y podrás cocinar con ella, pero quedará
          marcada como <strong>sin costo registrado</strong> para no alterar tus promedios.
        </Banner>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar">
          {error}
        </Banner>
      )}

      <div className="space-y-2">
        <Button tamano="grande" disabled={guardando || !valida} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Registrar saldo inicial'}
        </Button>
        <Falta faltan={faltan} />
        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
